import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  runBootstrapFinalize,
  submitBootstrapNetwork,
  type BootstrapNetworkPayload,
  type OnlineInterface,
} from '../../bootstrap/api';
import { refreshBootstrapState } from '../../bootstrap/useBootstrapGate';
import { useBootstrapStore } from '../../store/bootstrapStore';

type WizardStep = 'setup' | 'done';
type NetworkMode = 'dhcp' | 'static' | 'skip';

interface StaticForm {
  address: string;
  prefix: string;
  gateway: string;
  dns: string;
}

const EMPTY_STATIC: StaticForm = {
  address: '',
  prefix: '24',
  gateway: '',
  dns: '',
};

const FINALIZE_POLL_MS = 1500;

export function WelcomePage() {
  const state = useBootstrapStore((s) => s.state);
  const status = useBootstrapStore((s) => s.status);
  const [step, setStep] = useState<WizardStep>(() => (status === 'completed' ? 'done' : 'setup'));

  useEffect(() => {
    if (status === 'completed' && step !== 'done') setStep('done');
  }, [status, step]);

  const online = state?.online_interfaces ?? [];

  return (
    <div className="welcome-wallpaper" data-testid="welcome-page">
      <div className="welcome-card">
        <WizardStepHeader step={step} />
        {step === 'setup' ? (
          <SetupStep online={online} onDone={() => setStep('done')} />
        ) : (
          <DoneStep />
        )}
      </div>
    </div>
  );
}

const STEP_TITLES: Record<WizardStep, string> = {
  setup: '欢迎与网络',
  done: '完成',
};

const STEP_ORDER: WizardStep[] = ['setup', 'done'];

function WizardStepHeader({ step }: { step: WizardStep }) {
  const idx = STEP_ORDER.indexOf(step);
  return (
    <div className="space-y-3">
      <div className="welcome-steps">
        {STEP_ORDER.map((s, i) => {
          const cls =
            i < idx ? 'welcome-step-dot done' : i === idx ? 'welcome-step-dot active' : 'welcome-step-dot';
          return <span key={s} className={cls} />;
        })}
      </div>
      <div>
        <p className="welcome-muted">
          步骤 {idx + 1} / {STEP_ORDER.length} · {STEP_TITLES[step]}
        </p>
        <h1 className="mt-1 text-2xl font-semibold">{STEP_TITLES[step]}</h1>
      </div>
    </div>
  );
}

function SetupStep({
  online,
  onDone,
}: {
  online: OnlineInterface[];
  onDone: () => void;
}) {
  const state = useBootstrapStore((s) => s.state);
  const [selected, setSelected] = useState<string>(() => (online.length === 1 ? (online[0]?.name ?? '') : ''));
  const [mode, setMode] = useState<NetworkMode>(online.length === 0 ? 'skip' : 'dhcp');
  const [staticForm, setStaticForm] = useState<StaticForm>(EMPTY_STATIC);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef<number | null>(null);

  const branch: 'A' | 'B' | 'C' = useMemo(() => {
    if (online.length === 0) return 'B';
    if (online.length === 1) return 'A';
    return 'C';
  }, [online.length]);

  useEffect(() => {
    if (!state?.completed) return;
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    setSubmitting(false);
    onDone();
  }, [onDone, state?.completed]);

  // 组件卸载时清理轮询计时器，避免用户离开 /welcome 后 finalize poll 仍在后台打 API
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  async function kickFinalize() {
    try {
      await runBootstrapFinalize();
    } catch {
      // finalize 的错误会在 refresh 后写回 store
    }

    // 不放在 finally 里:finally 内 return 会吞 try/catch 的异常(no-unsafe-finally)。
    // 当前 catch 是显式吞的,把后续逻辑移到 finally 之后语义等价、且更安全。
    await refreshBootstrapState();
    const latest = useBootstrapStore.getState().state;
    if (latest?.completed) {
      setSubmitting(false);
      onDone();
      return;
    }
    if (latest?.finalize_stage === 'error') {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
      timerRef.current = null;
      setError(latest.finalize_error ?? '初始化失败');
      setSubmitting(false);
    }
  }

  function buildPayload(): BootstrapNetworkPayload | null {
    if (mode === 'skip') {
      return { mode: 'skip' };
    }
    if (!selected) {
      setError('请先选择一张网卡');
      return null;
    }
    if (mode === 'dhcp') {
      return { mode: 'dhcp', interface: selected };
    }

    const prefixNum = Number(staticForm.prefix);
    if (!staticForm.address || !staticForm.gateway || !prefixNum) {
      setError('静态 IP 需要填写地址、掩码、网关');
      return null;
    }
    return {
      mode: 'static',
      interface: selected,
      ipv4: {
        address: staticForm.address,
        prefix: prefixNum,
        gateway: staticForm.gateway,
        dns: staticForm.dns
          ? staticForm.dns.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      },
    };
  }

  async function submit() {
    setError(null);
    const payload = buildPayload();
    if (!payload) return;

    setSubmitting(true);
    try {
      await submitBootstrapNetwork(payload);
      await kickFinalize();
      if (timerRef.current === null) {
        timerRef.current = window.setInterval(() => {
          void kickFinalize();
        }, FINALIZE_POLL_MS);
      }
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof Error ? err.message : '提交失败');
    }
  }

  return (
    <div className="space-y-4" data-testid="welcome-step-setup">
      <p className="text-sm leading-6" style={{ color: 'rgba(247,249,255,0.78)' }}>
        欢迎使用 AgentOS。现在完成首启网络初始化，系统会在提交后自动应用配置并同步访问入口。
        完成后即可进入桌面，账号登录统一通过 Casdoor SSO 处理。
      </p>

      {branch === 'B' ? (
        <div>
          <p className="text-sm text-foreground-muted">
            当前没有检测到在线的有线网卡。可以插入网线后刷新页面，或先跳过，登录后再进入“网络配置”APP 补配。
          </p>
          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              type="button"
              className="welcome-btn ghost"
              onClick={() => window.location.reload()}
            >
              重新检测
            </button>
            <button
              type="button"
              data-testid="welcome-skip-network"
              className="welcome-btn primary"
              disabled={submitting}
              onClick={() => void submit()}
            >
              {submitting ? '处理中…' : '跳过网络配置'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="welcome-step-network">
          <InterfacePicker
            online={online}
            selected={selected}
            onChange={setSelected}
            autoSelected={branch === 'A'}
          />
          <ModePicker mode={mode} onChange={setMode} />
          {mode === 'static' && <StaticFormFields value={staticForm} onChange={setStaticForm} />}
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="welcome-submit-network"
              className="welcome-btn primary"
              disabled={submitting || !selected}
              onClick={() => void submit()}
            >
              {submitting ? '应用中…' : '完成初始化'}
            </button>
          </div>
        </div>
      )}

      {(submitting || state?.finalize_stage === 'error' || error) && (
        <div className="space-y-4">
          <StageList stage={state?.finalize_stage ?? 'idle'} />
          {error && (
            <div
              role="alert"
              data-testid="welcome-finalize-error"
              style={{
                borderRadius: 12,
                border: '1px solid rgba(255, 138, 138, 0.4)',
                background: 'rgba(255, 60, 60, 0.08)',
                padding: 12,
                fontSize: 13,
              }}
            >
              <p className="font-medium" style={{ color: '#ff8a8a' }}>
                初始化失败
              </p>
              <p className="mt-1 break-all welcome-muted">{error}</p>
              <button
                type="button"
                data-testid="welcome-finalize-retry"
                className="welcome-btn primary"
                style={{ marginTop: 12, minWidth: 0, padding: '6px 14px', fontSize: 12 }}
                disabled={submitting}
                onClick={() => void submit()}
              >
                重试
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InterfacePicker({
  online,
  selected,
  onChange,
  autoSelected,
}: {
  online: OnlineInterface[];
  selected: string;
  onChange: (name: string) => void;
  autoSelected: boolean;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium mb-2">
        主访问网卡
        {autoSelected && <span className="ml-2 welcome-muted">(已自动选中)</span>}
      </legend>
      <div className="space-y-2">
        {online.map((iface) => {
          const checked = selected === iface.name;
          return (
            <label
              key={iface.name}
              className={checked ? 'welcome-option checked' : 'welcome-option'}
            >
              <input
                type="radio"
                name="welcome-interface"
                value={iface.name}
                checked={checked}
                onChange={() => onChange(iface.name)}
              />
              <span className="font-medium">{iface.name}</span>
              {iface.ipv4?.address && (
                <span className="welcome-muted">
                  {iface.ipv4.address}/{iface.ipv4.prefix ?? '?'}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function ModePicker({
  mode,
  onChange,
}: {
  mode: NetworkMode;
  onChange: (m: NetworkMode) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium mb-2">IP 模式</legend>
      <div className="flex gap-2 text-sm">
        <label className={mode === 'dhcp' ? 'welcome-option checked' : 'welcome-option'}>
          <input
            type="radio"
            name="welcome-mode"
            checked={mode === 'dhcp'}
            onChange={() => onChange('dhcp')}
          />
          DHCP 自动获取
        </label>
        <label className={mode === 'static' ? 'welcome-option checked' : 'welcome-option'}>
          <input
            type="radio"
            name="welcome-mode"
            checked={mode === 'static'}
            onChange={() => onChange('static')}
          />
          静态 IP
        </label>
      </div>
    </fieldset>
  );
}

function StaticFormFields({
  value,
  onChange,
}: {
  value: StaticForm;
  onChange: (v: StaticForm) => void;
}) {
  const set = (key: keyof StaticForm) => (e: ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <label className="welcome-label">
        <span>IP 地址</span>
        <input type="text" className="welcome-input" value={value.address} onChange={set('address')} />
      </label>
      <label className="welcome-label">
        <span>掩码位</span>
        <input
          type="number"
          min={1}
          max={32}
          className="welcome-input"
          value={value.prefix}
          onChange={set('prefix')}
        />
      </label>
      <label className="welcome-label">
        <span>网关</span>
        <input type="text" className="welcome-input" value={value.gateway} onChange={set('gateway')} />
      </label>
      <label className="welcome-label">
        <span>DNS(逗号分隔)</span>
        <input type="text" className="welcome-input" value={value.dns} onChange={set('dns')} />
      </label>
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  idle: '准备中',
  applying_network: '配置网络',
  syncing_casdoor: '同步账号',
  writing_discovery: '写入发现配置',
  marking_completed: '标记完成',
  done: '完成',
  error: '失败',
};

function StageList({ stage }: { stage: string }) {
  const stages = ['applying_network', 'syncing_casdoor', 'writing_discovery', 'marking_completed'];
  const idx = stages.indexOf(stage);
  return (
    <ol className="space-y-1 text-sm" data-testid="welcome-stage-list">
      {stages.map((s, i) => {
        const reached = idx >= i || stage === 'done';
        return (
          <li
            key={s}
            style={{
              color: reached ? '#f7f9ff' : 'rgba(247,249,255,0.45)',
            }}
          >
            {reached ? '✓' : '•'} {STAGE_LABELS[s]}
          </li>
        );
      })}
    </ol>
  );
}

function DoneStep() {
  const state = useBootstrapStore((s) => s.state);
  const skipped = !state?.access_url;
  return (
    <div className="space-y-4" data-testid="welcome-step-done">
      <p className="text-sm">初始化完成。</p>
      {skipped ? (
        <div
          style={{
            borderRadius: 12,
            border: '1px solid rgba(251, 191, 36, 0.4)',
            background: 'rgba(251, 191, 36, 0.08)',
            padding: 12,
            fontSize: 13,
          }}
        >
          <p className="font-medium" style={{ color: '#fbbf24' }}>
            尚未配置网络
          </p>
          <p className="mt-1 welcome-muted">当前仅支持盒子本地使用。登录后打开“网络配置”APP 即可补配。</p>
        </div>
      ) : (
        <p className="text-sm">
          访问地址:
          <a
            className="ml-1 underline"
            style={{ color: '#7aa2ff' }}
            href={state?.access_url ?? '#'}
            data-testid="welcome-access-url"
          >
            {state?.access_url}
          </a>
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          className="welcome-btn primary"
          onClick={() => {
            window.location.href = '/';
          }}
        >
          进入系统
        </button>
      </div>
    </div>
  );
}
